import {
  clampMotionSequenceTime,
  createMotionViewportCanvas,
  getEffectiveMotionPlaybackDuration,
  getMotionAtlasFrameTime,
  getMotionFrameIndex,
  getMotionResizeDimensions,
  interpolateTrackingQuadAtTime,
  renderFrame,
  seekMotionVideos,
  type LoadedMotionAssets,
  type MotionRenderOptions
} from './motion';
import { VideoDecoderPipeline } from './VideoDecoderPipeline';
import { UserVideoDecoderPipeline } from './UserVideoDecoderPipeline';
import { EncoderBackpressureGate } from './EncoderBackpressureGate';
import {
  getRenderAbortError,
  throwIfRenderAborted,
  waitForRenderOperation,
  yieldToRenderControls
} from './RenderTask';
import { StreamingMp4Muxer } from './streamingMp4Muxer';
import type { VideoExportArtifact } from './videoExportStorage';
import type { MotionResizeMode } from '../types';

const MOTION_EXPORT_SIZE = 1080;
const DEFAULT_MOTION_GOP = 30;
const MOTION_EXPORT_SCREEN_RENDER_SCALE = 2;
const MOTION_EXPORT_BITRATE = 12_000_000;

export interface MotionVideoExportOptions extends MotionRenderOptions {
  resizeMode?: MotionResizeMode;
  onProgress?: (completedFrames: number, totalFrames: number) => void;
  onStageChange?: (stage: 'rendering' | 'packaging') => void;
  /** URL of the user-supplied video (blob: or https:). When provided the export
   *  uses a fast WebCodecs sequential decoder instead of HTMLVideoElement.seek. */
  userSourceUrl?: string;
  outputFileHandle?: FileSystemFileHandle | null;
  signal?: AbortSignal;
}

export function isMotionVideoExportSupported(): boolean {
  return typeof VideoEncoder !== 'undefined' && typeof VideoFrame !== 'undefined';
}

/**
 * Tries H.264 codec profiles from most compatible to most capable until one
 * passes VideoEncoder.isConfigSupported(). Handles browsers that throw instead
 * of returning {supported:false} (e.g. older Safari).
 */
async function findSupportedEncoderConfig(
  baseConfig: VideoEncoderConfig
): Promise<VideoEncoderConfig> {
  if (typeof VideoEncoder.isConfigSupported !== 'function') {
    return baseConfig;
  }

  // avc1.4d0028 = Main Profile Level 4.0  — handles 1080×1080@30 fps, supported on mobile/Safari
  // avc1.42C028 = Baseline Profile Level 4.0 — most universally compatible fallback
  // avc1.4d0033 = Main Profile Level 5.1    — original (desktop Chrome/Firefox)
  const codecCandidates = ['avc1.4d0028', 'avc1.42C028', 'avc1.4d0033'];
  // Safari may reject 'prefer-hardware' when HW is unavailable for a given codec+resolution.
  // Try hardware first (faster), then fall back to software.
  const hwCandidates: Array<VideoEncoderConfig['hardwareAcceleration']> = [
    'prefer-hardware',
    'no-preference'
  ];

  for (const hardwareAcceleration of hwCandidates) {
    for (const codec of codecCandidates) {
      const config: VideoEncoderConfig = { ...baseConfig, codec, hardwareAcceleration };
      try {
        const support = await VideoEncoder.isConfigSupported(config);
        if (support.supported) {
          return config;
        }
      } catch {
        // isConfigSupported throws in some browsers (e.g. older Safari) — try next.
      }
    }
  }

  throw new Error(
    'WebCodecs H.264 encoder не поддерживается в данном браузере. ' +
    'Попробуйте Chrome или обновите браузер.'
  );
}

export async function renderMotionExport(
  motionAssets: LoadedMotionAssets,
  userSource: CanvasImageSource,
  options: MotionVideoExportOptions = {}
): Promise<VideoExportArtifact> {
  throwIfRenderAborted(options.signal);
  if (!isMotionVideoExportSupported()) {
    throw new Error(
      'Для экспорта анимации откройте сервис во внешнем браузере или обновите систему на свежую версию.'
    );
  }

  const fps = 30; // Жестко ограничиваем до 30 кадров для стабильной выдачи
  const duration = getEffectiveMotionPlaybackDuration(motionAssets.tracking, userSource);
  const totalFrames = Math.max(1, Math.ceil(duration * fps));
  const resizeMode = options.resizeMode ?? '1:1';
  const internalSquareRenderSize = Math.max(
    1,
    Math.round(options.renderSize ?? (resizeMode !== '1:1' ? motionAssets.template.outputSize || 1920 : MOTION_EXPORT_SIZE))
  );
  const exportViewport = getMotionResizeDimensions(resizeMode, internalSquareRenderSize);
  const encoderConfig = await waitForRenderOperation(
    findSupportedEncoderConfig({
      codec: 'avc1.4d0028', // start with widely-compatible Main Profile Level 4.0
      width: exportViewport.outputWidth,
      height: exportViewport.outputHeight,
      bitrate: MOTION_EXPORT_BITRATE,
      framerate: fps,
      hardwareAcceleration: 'prefer-hardware',
      avc: {
        format: 'avc'
      }
    }),
    {
      signal: options.signal,
      label: 'Video encoder capability check'
    }
  );

  let encoderGate: EncoderBackpressureGate | null = null;
  let muxer: StreamingMp4Muxer | null = null;
  const encoder = new VideoEncoder({
    output: (chunk, meta) => {
      try {
        if (!muxer) {
          throw new Error('MP4 streaming output is not initialized.');
        }
        muxer.enqueueVideoChunk(chunk, meta);
        encoderGate?.notifyOutput();
      } catch (error) {
        encoderGate?.fail(error);
      }
    },
    error: (error) => {
      encoderGate?.fail(error);
    }
  });
  encoderGate = new EncoderBackpressureGate(() => encoder.encodeQueueSize);

  const atlasUrl = motionAssets.atlasVideo.currentSrc || motionAssets.atlasVideo.src;
  const pipeline = new VideoDecoderPipeline(atlasUrl, options.signal);
  let userPipeline: UserVideoDecoderPipeline | null = null;
  const handleAbort = () => {
    encoderGate?.fail(getRenderAbortError(options.signal));
  };
  options.signal?.addEventListener('abort', handleAbort, { once: true });

  try {
    muxer = await StreamingMp4Muxer.create({
      totalFrames,
      frameRate: fps,
      bitrate: MOTION_EXPORT_BITRATE,
      durationSeconds: duration,
      outputFileHandle: options.outputFileHandle,
      signal: options.signal,
      onError: (error) => encoderGate?.fail(error)
    });
    encoderGate.throwIfUnavailable(options.signal);
    encoder.configure(encoderConfig);
    await pipeline.initialize();
    encoderGate.throwIfUnavailable(options.signal);

    // Fast user-video path: sequential WebCodecs decoder avoids per-frame HTMLVideoElement.seek.
    if (userSource instanceof HTMLVideoElement && options.userSourceUrl) {
      userPipeline = new UserVideoDecoderPipeline(options.userSourceUrl, options.signal);
      await userPipeline.initialize();
      encoderGate.throwIfUnavailable(options.signal);
    }

    options.onStageChange?.('rendering');
    for (let frameIndex = 0; frameIndex < totalFrames; frameIndex += 1) {
      encoderGate.throwIfUnavailable(options.signal);
      const time = frameIndex / fps;
      const trackingTime = clampMotionSequenceTime(motionAssets.tracking, time);
      const trackingFrameIndex = getMotionFrameIndex(motionAssets.tracking, trackingTime);
      const atlasTime = getMotionAtlasFrameTime(motionAssets.tracking, trackingFrameIndex);

      let decoderFrame: VideoFrame | null = null;
      let userFrame: VideoFrame | null = null;
      let encodedFrame: VideoFrame | null = null;
      try {
        decoderFrame = await pipeline.seekToTime(atlasTime);
        if (!decoderFrame) {
          throw new Error('Не удалось извлечь кадр из VideoDecoder (поток оборван).');
        }

        // Resolve user video frame for this timestamp.
        let effectiveUserSource: CanvasImageSource | null = userSource;
        if (userPipeline) {
          userFrame = await userPipeline.seekToTime(time);
          if (userFrame) {
            effectiveUserSource = userFrame;
          }
          // If userFrame is null the pipeline has no frames at all — fall back to HTMLVideoElement.
        } else {
          await seekMotionVideos(null, userSource, time, options.signal);
        }

        const trackingQuad = interpolateTrackingQuadAtTime(motionAssets.tracking, trackingTime);
        const squareCanvas = renderFrame(motionAssets, effectiveUserSource, trackingFrameIndex, {
          ...options,
          renderSize: internalSquareRenderSize,
          screenRenderScale: options.screenRenderScale ?? MOTION_EXPORT_SCREEN_RENDER_SCALE,
          atlasSourceOverride: decoderFrame,
          trackingQuadOverride: trackingQuad,
          realtime: false
        });
        const canvas = createMotionViewportCanvas(squareCanvas, resizeMode, {
          squareRenderSize: internalSquareRenderSize,
          targetWidth: exportViewport.outputWidth,
          targetHeight: exportViewport.outputHeight
        });

        const timestamp = Math.round((frameIndex * 1_000_000) / fps);
        const frameDuration = Math.round(1_000_000 / fps);
        encodedFrame = new VideoFrame(canvas, {
          timestamp,
          duration: frameDuration
        });

        encoder.encode(encodedFrame, {
          keyFrame: frameIndex % DEFAULT_MOTION_GOP === 0
        });
      } finally {
        encodedFrame?.close();
        userFrame?.close();
        decoderFrame?.close();
      }
      await Promise.all([
        encoderGate.waitForCapacity(options.signal),
        muxer.waitForCapacity()
      ]);

      options.onProgress?.(frameIndex + 1, totalFrames);
      if (frameIndex % 2 === 1) {
        await yieldToRenderControls(options.signal);
      }
    }

    options.onStageChange?.('packaging');
    await encoderGate.waitForOperation(encoder.flush(), options.signal, 'Video packaging');
    encoderGate.throwIfUnavailable(options.signal);
    return await muxer.finalize();
  } finally {
    options.signal?.removeEventListener('abort', handleAbort);
    if (encoder.state !== 'closed') {
      encoder.close();
    }
    pipeline.close();
    userPipeline?.close();
    await muxer?.cancel();
  }
}
